import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MetaTags from 'components/common/MetaTags';
import PageHeader from 'components/NewDesign/PageHeader';
import MainContent from 'components/NewDesign/MainContent';
import LoadingContent from 'components/NewDesign/LoadingContent';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import SeasonChampions from 'components/admin/reports/SeasonChampions';
import styles from 'components/admin/reports/SeasonChampions/SeasonChampions.module.css';
import { getSeasonChampions, exportSeasonChampionsToFile } from 'api/reportsApi';
import { clubFeaturesOf } from 'utils/clubFeatures';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { teamWordsOf } from 'utils/teamLabel';

// Reports, Season Champions (points slice 6, docs/school-carnivals-points-slice-6.md in the portal repo):
// each group and gender's top 3 over the active season, for clubs and schools (Doug, 2026-10-26: "Better
// to have a season champions report"; "only show top 3 I think. We can change if asked. They can use the
// other report for more details"). The page asks the server once when it opens; the reply carries the
// season's name for the subtitle, so the page makes no other request.
const SeasonChampionsPage = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const clubId = selectedClub?.clubID;
  // A school's House column, in its word
  const teamWords = clubFeaturesOf(selectedClub).teams ? teamWordsOf(selectedClub) : null;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId) return undefined;
    // A reply for a club switched away from is dropped.
    let cancelled = false;
    setLoading(true);
    getSeasonChampions(clubId)
      .then((response) => {
        if (!cancelled) setReport(response.data);
      })
      .catch((err) => {
        console.error('[SeasonChampions] Fetch season champions error: ', err);
        if (!cancelled) {
          setReport(null);
          toast.error('Failed to load the season champions');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const hasChampions = (report?.lists || []).length > 0;

  const handleExportToFile = async () => {
    await exportAndDownloadFile(
      exportSeasonChampionsToFile,
      [clubId],
      '',
      'The season champions were successfully exported',
      'Fetching the season champions from the server...'
    );
  };

  return (
    <>
      <MetaTags title="SplitFast | Season Champions Report" />
      <MainContent>
        <PageHeader title="Season Champions" subTitle={report?.seasonName || ''} primarySubTitle>
          <CustomNewButton
            variant="info"
            onClick={handleExportToFile}
            label="Export to file"
            disabled={loading || !hasChampions}
            tooltip="Download the season champions as a CSV file."
          />
        </PageHeader>
        {!clubId ? (
          <div style={{ minHeight: '100vh' }} />
        ) : (
          <LoadingContent loading={loading}>
            {/* Nothing but the error message when the report could not be loaded. */}
            {hasChampions ? (
              <SeasonChampions lists={report.lists} teamWords={teamWords} />
            ) : (
              report !== null && (
                <div className={styles.card}>
                  <div className={styles.empty}>No season points yet.</div>
                </div>
              )
            )}
          </LoadingContent>
        )}
      </MainContent>
    </>
  );
};

export default SeasonChampionsPage;
